import React, { useState, useEffect } from 'react';
import {
  Package,
  PlusCircle,
  Calendar,
  DollarSign,
  CheckCircle2,
  Clock,
  Building,
  Phone,
  ShieldCheck,
  Tag,
  Repeat
} from 'lucide-react';
import { AssociationAsset, AssetRentalLog } from '../../types';
import { getAssociationAssetsDB, saveAssociationAssetDB, getAssetRentalLogsDB, saveAssetRentalLogDB } from '../../services/db';
import { useAuth } from '../../context/AuthContext';
import { SEO } from '../../components/common/SEO';
import { PageHeader } from '../../components/common/PageHeader';
import { isSuperAdmin } from '../../utils/formatters';

export const AssetRentalPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [assets, setAssets] = useState<AssociationAsset[]>([]);
  const [rentals, setRentals] = useState<AssetRentalLog[]>([]);
  const [activeTab, setActiveTab] = useState<'inventory' | 'rentals'>('inventory');

  // New Asset Modal
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [assetName, setAssetName] = useState('');
  const [category, setCategory] = useState<'Kelengkapan Silat' | 'Busana & Tanjak' | 'Senjata Berdaftar' | 'Peralatan Gelanggang / Mat' | 'Peralatan Media / PA System'>('Peralatan Gelanggang / Mat');
  const [quantity, setQuantity] = useState(10);
  const [estimatedValue, setEstimatedValue] = useState(1000);
  const [dailyRentalRate, setDailyRentalRate] = useState(50);
  const [fundedBy, setFundedBy] = useState('Geran MOTAC 2025');

  // Booking Modal
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<AssociationAsset | null>(null);
  const [renterName, setRenterName] = useState('');
  const [renterPhone, setRenterPhone] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [totalDays, setTotalDays] = useState(2);

  useEffect(() => {
    getAssociationAssetsDB().then(setAssets);
    getAssetRentalLogsDB().then(setRentals);
  }, []);

  const handleCreateAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assetName) return;

    const newAsset: AssociationAsset = {
      id: `ast-${Date.now()}`,
      assetName,
      category,
      acquiredDate: new Date().toISOString().split('T')[0],
      fundedBy,
      quantity: Number(quantity),
      estimatedValue: Number(estimatedValue),
      condition: 'Baik / Sedia Sewa',
      dailyRentalRate: Number(dailyRentalRate),
      assignedGelanggang: 'Semua Gelanggang'
    };

    const updated = await saveAssociationAssetDB(newAsset);
    setAssets(updated);
    setIsAssetModalOpen(false);
    setAssetName('');
  };

  const handleCreateRental = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAsset || !renterName) return;

    const endDateObj = new Date(startDate);
    endDateObj.setDate(endDateObj.getDate() + Number(totalDays));

    const newRental: AssetRentalLog = {
      id: `rnt-${Date.now()}`,
      assetId: selectedAsset.id,
      assetName: selectedAsset.assetName,
      renterName,
      renterPhone,
      startDate,
      endDate: endDateObj.toISOString().split('T')[0],
      totalDays: Number(totalDays),
      totalRentalFee: selectedAsset.dailyRentalRate * Number(totalDays),
      status: 'Disewakan / Sedang Digunakan',
      paymentStatus: 'Lulus / Dibayar'
    };

    const updated = await saveAssetRentalLogDB(newRental);
    setRentals(updated);
    setIsBookingModalOpen(false);
    setRenterName('');
  };

  const totalAssetValue = assets.reduce((sum, a) => sum + a.estimatedValue, 0);
  const totalRentalIncome = rentals
    .filter(r => r.paymentStatus === 'Lulus / Dibayar')
    .reduce((sum, r) => sum + r.totalRentalFee, 0);

  if (!isSuperAdmin(currentUser)) {
    return (
      <div className="p-8 text-center text-slate-500 font-bold bg-white rounded-3xl border border-slate-200">
        Akses modul Inventori Aset & Sewaan terhad kepada Superadmin sahaja buat masa ini.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SEO
        title="Inventori Aset & Sewaan | PSSASMM Kedah"
        description="Pengurusan Aset Persatuan Seni Silat Awang Sulung Merah Muda, rekod tempahan sewaan peralatan, dan penjanaan pendapatan berterusan (recurring income)."
      />

      {/* Standard PageHeader Component */}
      <PageHeader
        badgeText="Inventori & Hasil Sewaan"
        badgeIcon={Package}
        title="Inventori Aset & Rekod Sewaan"
      >
        <button
          onClick={() => setIsAssetModalOpen(true)}
          className="px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-bold text-xs rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
        >
          <PlusCircle className="w-4 h-4" />
          <span>Daftar Aset Baru</span>
        </button>
      </PageHeader>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500">Nilai Keseluruhan Aset NGO</span>
            <Package className="w-5 h-5 text-amber-600" />
          </div>
          <div className="text-xl font-black text-slate-900 font-mono">
            RM {totalAssetValue.toLocaleString('ms-MY', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-slate-400 font-medium">Pemilikan kekal persatuan</div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500">Pendapatan Sewaan Terkumpul</span>
            <Repeat className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="text-xl font-black text-emerald-600 font-mono">
            RM {totalRentalIncome.toLocaleString('ms-MY', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-slate-400 font-medium">Hasil sewaan pihak luar & sekolah</div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500">Akaun Bank Sah Persatuan</span>
            <Building className="w-5 h-5 text-slate-700" />
          </div>
          <div className="text-xs font-black text-slate-900 truncate">
            Bank Muamalat Malaysia Berhad
          </div>
          <div className="text-[10px] text-slate-500 font-mono font-bold">
            0203-0003921-71-0 (ROS: PPM0070228082022)
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab('inventory')}
          className={`px-4 py-2 rounded-xl font-extrabold text-xs transition-all cursor-pointer flex items-center gap-1.5 ${activeTab === 'inventory'
            ? 'bg-asmm-red text-white shadow-sm'
            : 'text-slate-600 hover:bg-slate-100'
            }`}
        >
          <Package className="w-4 h-4" />
          Senarai Aset NGO ({assets.length})
        </button>
        <button
          onClick={() => setActiveTab('rentals')}
          className={`px-4 py-2 rounded-xl font-extrabold text-xs transition-all cursor-pointer flex items-center gap-1.5 ${activeTab === 'rentals'
            ? 'bg-asmm-red text-white shadow-sm'
            : 'text-slate-600 hover:bg-slate-100'
            }`}
        >
          <Repeat className="w-4 h-4" />
          Rekod Tempahan Sewaan ({rentals.length})
        </button>
      </div>

      {/* TAB 1: ASSET INVENTORY */}
      {activeTab === 'inventory' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {assets.map((asset) => (
            <div key={asset.id} className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-3 flex flex-col justify-between">
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <span className="px-2.5 py-0.5 bg-slate-100 text-slate-700 text-[10px] font-black uppercase tracking-wider rounded-full border border-slate-200">
                    {asset.category}
                  </span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded-full">
                    {asset.condition}
                  </span>
                </div>

                <h3 className="font-extrabold text-slate-900 text-sm leading-snug">
                  {asset.assetName}
                </h3>

                <div className="text-xs text-slate-600 space-y-1 font-medium pt-1">
                  <div>Sumber Perolehan: <strong className="text-slate-900">{asset.fundedBy}</strong></div>
                  <div>Kuantiti: <strong className="text-slate-900">{asset.quantity} Unit / Keping</strong></div>
                  <div>Nilai Aset: <strong className="font-mono text-slate-900">RM {asset.estimatedValue.toLocaleString('ms-MY')}</strong></div>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                <div>
                  <span className="text-[10px] text-slate-400 font-bold block">KADAR SEWA:</span>
                  <span className="font-mono font-black text-emerald-700 text-sm">
                    RM {asset.dailyRentalRate}/Hari
                  </span>
                </div>
                <button
                  onClick={() => {
                    setSelectedAsset(asset);
                    setIsBookingModalOpen(true);
                  }}
                  className="px-3.5 py-1.5 bg-asmm-red hover:bg-asmm-crimson text-white font-bold text-xs rounded-xl transition-all shadow-xs cursor-pointer"
                >
                  Sewa Aset Ini
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TAB 2: RENTAL LOGS */}
      {activeTab === 'rentals' && (
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse border border-slate-200 rounded-xl overflow-hidden">
              <thead>
                <tr className="bg-slate-100 text-slate-900 font-extrabold text-[11px] border-b border-slate-300">
                  <th className="p-3 text-left">Aset Disewa</th>
                  <th className="p-3 text-left">Penyewa / Agensi</th>
                  <th className="p-3 text-center">Tempoh (Hari)</th>
                  <th className="p-3 text-right">Hasil Sewaan (RM)</th>
                  <th className="p-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {rentals.map((rental) => (
                  <tr key={rental.id} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="p-3 font-bold text-slate-900">{rental.assetName}</td>
                    <td className="p-3 font-medium text-slate-800">
                      <div>{rental.renterName}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{rental.renterPhone}</div>
                    </td>
                    <td className="p-3 text-center font-mono font-bold">{rental.totalDays} Hari</td>
                    <td className="p-3 text-right font-mono font-black text-emerald-700">
                      RM {rental.totalRentalFee.toFixed(2)}
                    </td>
                    <td className="p-3 text-center">
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-black text-[10px] uppercase">
                        {rental.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CREATE ASSET MODAL */}
      {isAssetModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full space-y-4 border border-slate-200 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="font-extrabold text-slate-900 text-base">Pendaftaran Aset Persatuan</h3>
              <button onClick={() => setIsAssetModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-black text-sm">✕</button>
            </div>

            <form onSubmit={handleCreateAsset} className="space-y-3">
              <div>
                <label className="text-xs font-extrabold text-slate-700 block mb-1">Nama Aset / Peralatan</label>
                <input
                  type="text"
                  required
                  value={assetName}
                  onChange={(e) => setAssetName(e.target.value)}
                  placeholder="Contoh: Mat Gelanggang Getah EVA Standard"
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-medium"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-extrabold text-slate-700 block mb-1">Kategori Aset</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as any)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-bold"
                  >
                    <option value="Peralatan Gelanggang / Mat">Peralatan Mat / Gelanggang</option>
                    <option value="Busana & Tanjak">Busana & Tanjak</option>
                    <option value="Senjata Berdaftar">Senjata Berdaftar</option>
                    <option value="Kelengkapan Silat">Kelengkapan Silat</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-extrabold text-slate-700 block mb-1">Sumber Dana</label>
                  <input
                    type="text"
                    required
                    value={fundedBy}
                    onChange={(e) => setFundedBy(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-bold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-extrabold text-slate-700 block mb-1">Kuantiti</label>
                  <input
                    type="number"
                    required
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="text-xs font-extrabold text-slate-700 block mb-1">Kadar Sewa/Hari (RM)</label>
                  <input
                    type="number"
                    required
                    value={dailyRentalRate}
                    onChange={(e) => setDailyRentalRate(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-mono font-bold text-emerald-700"
                  />
                </div>
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAssetModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 font-bold text-xs rounded-xl"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-black text-xs rounded-xl shadow-md"
                >
                  Daftar Aset
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* BOOKING MODAL */}
      {isBookingModalOpen && selectedAsset && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full space-y-4 border border-slate-200 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="font-extrabold text-slate-900 text-base">Rekod Tempahan Sewaan Aset</h3>
              <button onClick={() => setIsBookingModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-black text-sm">✕</button>
            </div>

            <form onSubmit={handleCreateRental} className="space-y-3">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                <div className="text-[10px] text-slate-400 font-bold uppercase">Aset Dipilih:</div>
                <div className="font-extrabold text-slate-900 text-xs">{selectedAsset.assetName}</div>
                <div className="font-mono text-emerald-700 text-xs font-bold mt-0.5">Kadar: RM {selectedAsset.dailyRentalRate}/Hari</div>
              </div>

              <div>
                <label className="text-xs font-extrabold text-slate-700 block mb-1">Nama Penyewa / Agensi / Sekolah</label>
                <input
                  type="text"
                  required
                  value={renterName}
                  onChange={(e) => setRenterName(e.target.value)}
                  placeholder="Contoh: Produksi Filem Warisan / SMK Kulim"
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-medium"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-extrabold text-slate-700 block mb-1">No. Telefon</label>
                  <input
                    type="text"
                    required
                    value={renterPhone}
                    onChange={(e) => setRenterPhone(e.target.value)}
                    placeholder="019-1234567"
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="text-xs font-extrabold text-slate-700 block mb-1">Bilangan Hari</label>
                  <input
                    type="number"
                    required
                    value={totalDays}
                    onChange={(e) => setTotalDays(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-mono font-bold"
                  />
                </div>
              </div>

              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between text-xs font-black text-emerald-900">
                <span>Jumlah Hasil Sewaan:</span>
                <span className="font-mono text-sm">RM {(selectedAsset.dailyRentalRate * totalDays).toFixed(2)}</span>
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsBookingModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 font-bold text-xs rounded-xl"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 text-white font-black text-xs rounded-xl shadow-md"
                >
                  Sahkan Tempahan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
